<template>
  <v-dialog v-model="model" scrollable fullscreen>
    <v-card v-if="companyData">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <img
            src="https://restorani.bitlab.host/img/o.png"
            alt="logo"
            style="width: 54px"
          />
          <div class="ml-6">
            <div>{{ titleText }} {{ $t("meals").toLowerCase() }}</div>
            <div class="subtitle-1 font-weight-bold">
              {{ companyData.menu }}, ID {{ companyData.id }}
            </div>
          </div>
          <div class="primary--text ml-6">
            <v-icon color="primary" small>mdi-calendar</v-icon>
            {{ formattedMonthName }} {{ selectedYear }}.
          </div>
        </div>
        <circle-button @click="model = false" icon="mdi-close" />
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-row align="center" justify="space-between" class="mb-4">
          <v-col>
            <v-row align="center">
              <v-col cols="auto">
                <month-picker v-model="selectedDate" />
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                  v-model="showAllDays"
                  :label="$t('show_all_days')"
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                  v-model="showOrderedMeals"
                  :label="$t('ordered_1')"
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                  v-if="canViewTakenMeals"
                  v-model="showTakenMeals"
                  :label="$t('taken')"
                  hide-details
                  dense
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="auto">
            <v-row no-gutters>
              <excel-button @click="exportToExcel" />
              <print-button @click="printReportDocument" class="ml-2" />
            </v-row>
          </v-col>
        </v-row>
        <v-data-table
          v-if="!loading"
          :headers="tableHeaders"
          :items="processedTableData"
          :items-per-page="-1"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
          dense
        >
          <template
            v-for="col in ['totalOrdered', 'totalTaken', 'difference']"
            v-slot:[`item.${col}`]="{ item }"
          >
            <strong>{{ item[col] || 0 }}</strong>
          </template>
          <template
            v-for="shift in ['1-smena', '2-smena', '3-smena', '4-smena']"
            v-slot:[`item.${shift}.ordered`]="{ item }"
          >
            {{ item[shift]?.ordered || 0 }}
          </template>
          <template
            v-for="shift in ['1-smena', '2-smena', '3-smena', '4-smena']"
            v-slot:[`item.${shift}.taken`]="{ item }"
          >
            {{ item[shift]?.taken || 0 }}
          </template>
        </v-data-table>
        <v-row v-else justify="center">
          <v-progress-circular indeterminate color="primary" size="24" />
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import moment from "moment";
import hasPrinting from "@/mixins/hasPrinting";
import reportService from "@/services/report";
import hasSnackbar from "@/mixins/hasSnackbar";

const SHIFTS = ["1-smena", "2-smena", "3-smena", "4-smena"];

export default {
  mixins: [hasModel, hasExcel, hasPrinting, hasSnackbar],
  name: "ReportMealsTakenCompanyMonthlyOrder",
  props: {
    companyData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      showTakenMeals: false,
      showOrderedMeals: true,
      showAllDays: false,
      selectedDate: new Date().toISOString().substr(0, 7),
      apiResponseData: [],
    };
  },
  computed: {
    titleText() {
      const parts = [];
      if (this.showOrderedMeals) parts.push(this.$t("ordered_12"));
      if (this.showTakenMeals) parts.push(this.$t("taken1"));
      return parts.join("/");
    },
    selectedMonth() {
      return parseInt(this.selectedDate.split("-")[1]);
    },
    selectedYear() {
      return parseInt(this.selectedDate.split("-")[0]);
    },
    formattedMonthName() {
      return moment(this.selectedDate, "YYYY-MM")
        .locale(this.locale)
        .format("MMMM")
        .toUpperCase();
    },
    canViewTakenMeals() {
      return (
        this.companyData.settings?.can_see_ordered ||
        this.$store.state.user.user?.type === "restaurant"
      );
    },
    exportFileName() {
      return `${this.companyData.menu} ${this.titleText} ${this.$t(
        "meals"
      ).toLowerCase()}`;
    },
    availableShifts() {
      return SHIFTS.filter(
        (shift) => this.apiResponseData.length && this.apiResponseData[0]?.[shift]
      );
    },
    tableHeaders() {
      const headers = [
        { text: this.$t("day_hours"), value: "day", sortable: false, width: "15%" },
      ];

      if (this.showOrderedMeals) {
        this.availableShifts.forEach((shift) => {
          headers.push({
            text: this.$t(`shift_${shift.charAt(0)}_ordered`),
            value: `${shift}.ordered`,
            align: "center",
            sortable: false,
          });
        });
        headers.push({
          text: this.$t("total_ordered"),
          value: "totalOrdered",
          align: "center",
          sortable: false,
        });
      }

      if (this.showTakenMeals) {
        this.availableShifts.forEach((shift) => {
          headers.push({
            text: this.$t(`shift_${shift.charAt(0)}_taken`),
            value: `${shift}.taken`,
            align: "center",
            sortable: false,
          });
        });
        headers.push({
          text: this.$t("total_taken"),
          value: "totalTaken",
          align: "center",
          sortable: false,
        });
      }

      if (this.showOrderedMeals && this.showTakenMeals) {
        headers.push({
          text: this.$t("difference"),
          value: "difference",
          align: "center",
          sortable: false,
        });
      }
      return headers;
    },
    locale() {
      let locale = "";
      switch (this.$i18n.locale) {
        case "en":
          locale = "en-GB";
          break;
        case "hr":
          locale = "hr-HR";
          break;
        case "sr":
          locale = "sr-RS";
          break;
      }
      return locale;
    },
    processedTableData() {
      if (!this.apiResponseData.length) return [];

      // Kreiraj mapu postojećih dana
      const apiDataMap = new Map(this.apiResponseData.map((item) => [item.day, item]));
      const daysInMonth = moment({
        year: this.selectedYear,
        month: this.selectedMonth - 1,
      }).daysInMonth();

      // Totali za sve kolone
      const totals = {
        day: this.$t("totalU"),
        totalOrdered: 0,
        totalTaken: 0,
        difference: 0,
      };
      SHIFTS.forEach((shift) => {
        totals[shift] = { ordered: 0, taken: 0 };
      });

      const days = [];

      // Generiši dane
      for (let day = 1; day <= daysInMonth; day++) {
        const dayString = moment({
          year: this.selectedYear,
          month: this.selectedMonth - 1,
          day,
        }).format("DD-MM-YYYY");
        const apiData = apiDataMap.get(dayString) || { day: dayString };

        // Kreiraj dan sa svim shift podacima
        const dayRow = {
          ...apiData,
          day: `${day}.`,
          totalOrdered: 0,
          totalTaken: 0,
        };

        // Računaj totale za shiftove
        SHIFTS.forEach((shift) => {
          const ordered = apiData[shift]?.ordered || 0;
          const taken = apiData[shift]?.taken || 0;
          dayRow.totalOrdered += ordered;
          dayRow.totalTaken += taken;

          // Dodaj u grand totals
          totals[shift].ordered += ordered;
          totals[shift].taken += taken;
        });

        dayRow.difference = dayRow.totalOrdered - dayRow.totalTaken;

        // Filtriraj
        const hasData = dayRow.totalOrdered > 0 || dayRow.totalTaken > 0;

        if (this.showAllDays || hasData) {
          days.push(dayRow);
          totals.totalOrdered += dayRow.totalOrdered;
          totals.totalTaken += dayRow.totalTaken;
        }
      }

      // Dodaj total red
      if (days.length) {
        totals.difference = totals.totalOrdered - totals.totalTaken;
        days.push(totals);
      }

      return days;
    },
  },
  methods: {
    async loadCompanyMonthlyData() {
      if (!this.companyData?.id) return;
      this.loading = true;
      try {
        const monthStart = moment({
          year: this.selectedYear,
          month: this.selectedMonth - 1,
        });
        const { data } = await reportService.getShiftOrderStatus(
          this.$store.state.restaurant.id,
          {
            date_from: monthStart.format("YYYY-MM-DD"),
            date_to: monthStart.clone().endOf("month").format("YYYY-MM-DD"),
            company_id: this.companyData.id,
          }
        );
        this.apiResponseData = data || [];
      } catch (error) {
        this.apiResponseData = [];
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      this.downloadExcel({
        fileName: `${this.exportFileName}.xlsx`,
        sheets: [
          {
            name: "Report",
            headers: this.tableHeaders,
            items: this.processedTableData,
            title: this.exportFileName,
            subtitle: `${this.formattedMonthName} ${this.selectedYear}.`,
          },
        ],
      });
    },
    printReportDocument() {
      this.printDocument({
        title: this.exportFileName,
        subtitle: `${this.formattedMonthName} ${this.selectedYear}.`,
        tables: [{ headers: this.tableHeaders, items: this.processedTableData }],
      });
    },
  },
  watch: {
    model(isOpen) {
      if (isOpen) this.loadCompanyMonthlyData();
      else this.apiResponseData = [];
    },
    selectedDate() {
      if (this.model) this.loadCompanyMonthlyData();
    },
  },
};
</script>
